/**
 * <BugReportModal> — the submission form.
 *
 * Mounted only when the user clicks the floating "+" button. Receives
 * the screenshot already-captured (so the screenshot doesn't include
 * the modal chrome). If the capture failed, `screenshot` is null and
 * the modal shows a friendly "describe what you saw" message in the
 * preview pane.
 *
 * On submit:
 *   1. Show the one-per-session privacy gate (if not yet confirmed).
 *   2. POST /api/v1/bug-reports.
 *   3. Show a toast on success. Poll /:id for the github URL so the
 *      toast can be enriched with a "view on GitHub" link.
 *   4. On failure (other than rate-limit) keep the user's typed
 *      comment intact and surface the error inline.
 */

import { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Camera, Trash2, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  BUG_REPORT_STRINGS as S,
  MAX_TITLE_LENGTH,
  MAX_COMMENT_LENGTH,
} from './bug-report.strings';
import {
  submitBugReport,
  pollForGithubUrl,
  type BugSeverity,
} from './bug-report-api';
import type { BugReportMetadata } from './bug-report-metadata';
import type { CaptureResult } from './capture-provider';

const PRIVACY_CONFIRMED_KEY = 'bug-report.privacy-confirmed';

function hasConfirmedPrivacyThisSession(): boolean {
  try {
    return sessionStorage.getItem(PRIVACY_CONFIRMED_KEY) === '1';
  } catch {
    return false;
  }
}
function markPrivacyConfirmed(): void {
  try {
    sessionStorage.setItem(PRIVACY_CONFIRMED_KEY, '1');
  } catch {
    /* sessionStorage disabled — privacy gate will reshow, which is fine */
  }
}

export interface BugReportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The pre-captured screenshot, or null if capture failed. */
  screenshot: CaptureResult | null;
  /** Initial value for the Module field (derived from current route). */
  initialModule: string;
  /** Frozen metadata snapshot to submit. */
  metadata: BugReportMetadata;
  /** Async retake — opens the modal-less capture flow again. */
  onRetake: () => Promise<void> | void;
  /** Drop the screenshot from this submission. */
  onRemoveScreenshot: () => void;
}

export function BugReportModal(props: BugReportModalProps) {
  const {
    open,
    onOpenChange,
    screenshot,
    initialModule,
    metadata,
    onRetake,
    onRemoveScreenshot,
  } = props;

  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const [severity, setSeverity] = useState<BugSeverity>('p2');
  const [moduleField, setModuleField] = useState(initialModule);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [privacyGate, setPrivacyGate] = useState(false);
  const pollAbort = useRef<AbortController | null>(null);

  // Re-derive module if the route changed while the modal was closed.
  useEffect(() => {
    if (open) setModuleField(initialModule);
  }, [open, initialModule]);

  // Clean up any in-flight poll when the modal unmounts.
  useEffect(() => {
    return () => {
      pollAbort.current?.abort();
    };
  }, []);

  function validate(): string | null {
    if (!comment.trim()) return S.errorCommentRequired;
    if (comment.length > MAX_COMMENT_LENGTH) return S.errorCommentTooLong;
    if (title.length > MAX_TITLE_LENGTH) return S.errorTitleTooLong;
    return null;
  }

  function handleSubmitClick() {
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setError(null);
    if (!hasConfirmedPrivacyThisSession() && screenshot) {
      setPrivacyGate(true);
      return;
    }
    void doSubmit();
  }

  async function doSubmit() {
    setSubmitting(true);
    setError(null);

    try {
      const res = await submitBugReport({
        title: title.trim() || undefined,
        body: comment.trim(),
        severity,
        module: moduleField.trim() || undefined,
        screenshot: screenshot
          ? { base64: screenshot.base64, mimeType: screenshot.mimeType }
          : undefined,
        metadata,
      });

      // Optimistic toast — replaced once the GitHub URL is ready.
      const toastId = toast.success(S.toastSubmittedNoUrl, { duration: 4000 });

      // Reset and close. Polling continues in the background and only
      // ever ENHANCES the already-shown toast.
      setTitle('');
      setComment('');
      setSeverity('p2');
      onOpenChange(false);

      // Kick off poll.
      pollAbort.current?.abort();
      pollAbort.current = new AbortController();
      void pollForGithubUrl(res.id, { signal: pollAbort.current.signal }).then(
        (status) => {
          if (!status?.githubIssueUrl) return;
          const idLabel = status.githubIssueNumber ?? res.id;
          toast.success(S.toastSubmitted(idLabel), {
            id: toastId,
            duration: 8000,
            action: {
              label: S.toastViewOnGithub,
              onClick: () =>
                window.open(status.githubIssueUrl!, '_blank', 'noopener'),
            },
          });
        },
      );
    } catch (err: unknown) {
      const status = (err as { status?: number })?.status;
      const code = (err as { data?: { error?: { code?: string } } })?.data?.error
        ?.code;
      if (status === 429 || code === 'RATE_LIMITED') {
        setError(S.errorRateLimited);
      } else if (status === 403 || code === 'FEATURE_DISABLED') {
        setError(S.errorFeatureOff);
      } else {
        const msg = (err as { message?: string })?.message;
        setError(msg ? `${S.errorSubmitFailed} (${msg})` : S.errorSubmitFailed);
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => { if (!submitting) onOpenChange(v); }}>
        <DialogContent className="sm:max-w-[640px]">
          <DialogHeader>
            <DialogTitle>{S.modalTitle}</DialogTitle>
            <DialogDescription>{S.modalDescription}</DialogDescription>
          </DialogHeader>

          {/* Screenshot pane */}
          <div className="space-y-2">
            {screenshot ? (
              <div className="relative rounded-md border bg-muted/30 p-2">
                <img
                  src={`data:${screenshot.mimeType};base64,${screenshot.base64}`}
                  alt={S.screenshotPaneAlt}
                  className="block w-full h-auto max-h-[280px] object-contain rounded"
                  data-testid="bug-report-screenshot"
                />
                <div className="mt-2 flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => void onRetake()}
                    title={S.retake}
                    disabled={submitting}
                  >
                    <Camera className="mr-1.5 h-3.5 w-3.5" />
                    {S.retake}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={onRemoveScreenshot}
                    title={S.remove}
                    disabled={submitting}
                  >
                    <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                    {S.remove}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-2 rounded-md border bg-muted/30 p-3 text-sm text-muted-foreground">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{S.screenshotMissing}</span>
              </div>
            )}
          </div>

          {/* Title */}
          <div className="space-y-1.5">
            <Label htmlFor="bug-report-title">{S.titleLabel}</Label>
            <Input
              id="bug-report-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={S.titlePlaceholder}
              maxLength={MAX_TITLE_LENGTH}
              disabled={submitting}
            />
          </div>

          {/* Comment */}
          <div className="space-y-1.5">
            <Label htmlFor="bug-report-comment">{S.commentLabel}</Label>
            <Textarea
              id="bug-report-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={S.commentPlaceholder}
              rows={5}
              maxLength={MAX_COMMENT_LENGTH}
              disabled={submitting}
              data-testid="bug-report-comment"
            />
            <div className="text-xs text-muted-foreground text-right">
              {comment.length}/{MAX_COMMENT_LENGTH}
            </div>
          </div>

          {/* Severity + Module — side by side */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="bug-report-severity">{S.severityLabel}</Label>
              <Select
                value={severity}
                onValueChange={(v) => setSeverity(v as BugSeverity)}
                disabled={submitting}
              >
                <SelectTrigger id="bug-report-severity" data-testid="bug-report-severity">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="p0">{S.severityP0}</SelectItem>
                  <SelectItem value="p1">{S.severityP1}</SelectItem>
                  <SelectItem value="p2">{S.severityP2}</SelectItem>
                  <SelectItem value="p3">{S.severityP3}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bug-report-module">{S.moduleLabel}</Label>
              <Input
                id="bug-report-module"
                value={moduleField}
                onChange={(e) => setModuleField(e.target.value)}
                disabled={submitting}
              />
            </div>
          </div>

          {error && (
            <div
              role="alert"
              className={cn(
                'rounded-md border border-destructive/40 bg-destructive/10',
                'px-3 py-2 text-sm text-destructive',
              )}
              data-testid="bug-report-error"
            >
              {error}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              {S.cancel}
            </Button>
            <Button
              type="button"
              onClick={handleSubmitClick}
              disabled={submitting}
              data-testid="bug-report-submit"
            >
              {submitting ? S.submitting : S.submit}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Privacy gate — only shows once per session. */}
      <Dialog open={privacyGate} onOpenChange={setPrivacyGate}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>{S.privacyHeading}</DialogTitle>
            <DialogDescription>{S.privacyBody}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setPrivacyGate(false)}
            >
              {S.privacyCancel}
            </Button>
            <Button
              type="button"
              onClick={() => {
                markPrivacyConfirmed();
                setPrivacyGate(false);
                void doSubmit();
              }}
              data-testid="bug-report-privacy-confirm"
            >
              {S.privacyConfirm}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
